import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Alert, { type AlertColor } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Stack from "@mui/material/Stack";

// MUI-based replacement for Antd `notification` + `message`. Covers both:
// - message.success("saved")            -> notify("saved") / notify({ message, severity })
// - notification.error({ message, description }) -> notify({ severity, message, description })
// Supports stacking, auto-dismiss (or sticky), and replace-by-key for parity.

export type NotifySeverity = AlertColor; // success | info | warning | error

export interface NotifyOptions {
  message: ReactNode;
  severity?: NotifySeverity;
  /** Second line — Antd `notification`'s description. Omit for a simple `message`-style toast. */
  description?: ReactNode;
  /** ms before auto-dismiss; `null` keeps it until dismissed. Default 4000. */
  duration?: number | null;
  /** Stable key: re-notifying with the same key replaces the existing toast (Antd parity). */
  key?: string;
  onClick?: () => void;
  btn?: import("react").ReactNode;
  style?: import("react").CSSProperties;
  className?: string;
}

interface ActiveSnack extends NotifyOptions {
  id: string;
}

interface SnackbarContextValue {
  notify: (opts: NotifyOptions | string) => string;
  close: (id: string) => void;
}

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

export const useSnackbar = (): SnackbarContextValue => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error("useSnackbar must be used within a SnackbarProvider");
  return ctx;
};

const DEFAULT_DURATION = 4000;
const MAX_VISIBLE = 4;

const normalize = (opts: NotifyOptions | string): NotifyOptions =>
  typeof opts === "string" ? { message: opts } : opts;

// --- Imperative escape hatch (parity with Antd's global `notification`/`message`) ---
// Non-component code can `import { notify } from ".../SnackbarProvider"`. Calls made
// before a provider mounts are buffered and flushed on mount (not silently dropped).
let enqueueImpl: ((o: NotifyOptions) => string) | null = null;
const pending: NotifyOptions[] = [];

export const notify = (opts: NotifyOptions | string): string => {
  const o = normalize(opts);
  if (enqueueImpl) return enqueueImpl(o);
  pending.push(o);
  return o.key ?? "";
};

const SnackItem = ({
  snack,
  onClose,
}: {
  snack: ActiveSnack;
  onClose: (id: string) => void;
}) => {
  useEffect(() => {
    if (snack.duration === null) return undefined;
    const ms = snack.duration ?? DEFAULT_DURATION;
    const timer = setTimeout(() => onClose(snack.id), ms);
    return () => clearTimeout(timer);
  }, [snack, onClose]);

  return (
    <Alert
      severity={snack.severity ?? "info"}
      variant="filled"
      onClose={(event) => {
        // The close button lives inside the Alert, so dismissing would otherwise
        // also fire the toast's own onClick.
        event.stopPropagation();
        onClose(snack.id);
      }}
      onClick={snack.onClick}
      style={snack.style}
      sx={{ boxShadow: 3, width: "100%" }}
    >
      {snack.description ? <AlertTitle sx={{ mb: 0.5 }}>{snack.message}</AlertTitle> : snack.message}
      {snack.description}
    </Alert>
  );
};

export interface SnackbarProviderProps {
  children: ReactNode;
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

export const SnackbarProvider = ({
  children,
  anchorOrigin = { vertical: "top", horizontal: "right" },
}: SnackbarProviderProps) => {
  const [snacks, setSnacks] = useState<ActiveSnack[]>([]);
  const seq = useRef(0);

  const close = useCallback((id: string) => {
    setSnacks((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const notifyCb = useCallback((opts: NotifyOptions | string): string => {
    const o = normalize(opts);
    const id = o.key ?? `snack-${seq.current++}`;
    setSnacks((prev) => [...prev.filter((s) => s.id !== id), { ...o, id }].slice(-MAX_VISIBLE));
    return id;
  }, []);

  // Wire the imperative bridge to this provider; flush anything buffered pre-mount.
  useEffect(() => {
    enqueueImpl = notifyCb;
    while (pending.length) notifyCb(pending.shift()!);
    return () => {
      enqueueImpl = null;
    };
  }, [notifyCb]);

  const value = useMemo(() => ({ notify: notifyCb, close }), [notifyCb, close]);

  const posY = anchorOrigin.vertical === "top" ? { top: 16 } : { bottom: 16 };
  const posX =
    anchorOrigin.horizontal === "left"
      ? { left: 16 }
      : anchorOrigin.horizontal === "center"
        ? { left: "50%", transform: "translateX(-50%)" }
        : { right: 16 };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: "fixed",
          zIndex: (t) => t.zIndex.snackbar,
          width: "min(400px, calc(100vw - 32px))",
          ...posY,
          ...posX,
        }}
        aria-live="polite"
      >
        {snacks.map((s) => (
          <SnackItem key={s.id} snack={s} onClose={close} />
        ))}
      </Stack>
    </SnackbarContext.Provider>
  );
};
